import apiClient from './api-client';
import { getApiErrorMessage } from '../utils/api-error';

// ============================================================================
// API PATH CONSTANTS
// ============================================================================

const API_BASE = '/notices';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    BULK_ASSIGN: `${API_BASE}/bulk-assign`,
    BULK_ASSIGN_PREVIEW: `${API_BASE}/bulk-assign/preview`,
    DP_NOTICES: (dpId: number) => `${API_BASE}/dp/${dpId}`,
    ASSIGN: (id: number) => `${API_BASE}/${id}/assign`,
    ASSIGN_UNIFIED: `${API_BASE}/assign`, // Unified assignment endpoint
    ACKNOWLEDGE: (assignmentId: number) => `${API_BASE}/acknowledge/${assignmentId}`,
    ASSIGNMENT_BY_ID: (assignmentId: number) => `${API_BASE}/assignments/${assignmentId}`,
    RESEND_ASSIGNMENT: (assignmentId: number) => `${API_BASE}/assignments/${assignmentId}/resend`,
    ASSIGNMENT_STATUS: (assignmentId: number) => `${API_BASE}/assignments/${assignmentId}/status`,
    PURPOSE_STATUSES: `${API_BASE}/purpose-statuses`,
    VERIFY_OTP: (assignmentId: number) => `${API_BASE}/assignments/${assignmentId}/verify-otp`,
    RESEND_OTP: (assignmentId: number) => `${API_BASE}/assignments/${assignmentId}/resend-otp`,
} as const;

/** 429 resend throttle: seconds from JSON body or Retry-After header */
function parseNoticeResendRetryAfterSeconds(error: any): number {
    const fromBody = error?.response?.data?.retryAfterSeconds;
    if (typeof fromBody === 'number' && fromBody > 0 && fromBody < 86400) {
        return Math.ceil(fromBody);
    }
    const headers = error?.response?.headers;
    const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
    if (raw !== undefined && raw !== null && raw !== '') {
        const n = parseInt(String(raw), 10);
        if (!Number.isNaN(n) && n > 0 && n < 86400) return n;
    }
    return 300;
}

function formatNoticeResendWait(seconds: number): string {
    if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (s === 0) return `${m} minute${m !== 1 ? 's' : ''}`;
    return `${m} min ${s} sec`;
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Notice Type Enum
 */
export type NoticeType = 'BREACH_NOTICE' | 'ERASURE_NOTICE' | 'INFORMATIONAL_NOTICE';

/**
 * Notice Status Enum
 */
export type NoticeStatus = 'ACTIVE' | 'ARCHIVED';

/**
 * Content JSON structure for Breach Notice
 */
export interface BreachNoticeContent {
    title: string;
    affectedData: string;
    consequences: string;
    measuresTaken: string;
    recommendedPrecautions: string;
    contactInfo: string;
}

/**
 * Content JSON structure for Erasure Notice
 */
export interface ErasureNoticeContent {
    title: string;
    dataToBeErased: string;
    reason: string;
    retentionPeriod: string;
    otherFields?: Record<string, string>;
}

/**
 * Content JSON structure for Informational Notice
 */
export interface InformationalNoticeContent {
    title: string;
    lawfulGround: string;
    information: string;
}

/**
 * Multi-language content structure
 */
export type NoticeContentJSON = {
    [languageCode: string]: BreachNoticeContent | ErasureNoticeContent | InformationalNoticeContent;
};

/**
 * Full Notice interface (template only, no DP assignment)
 */
export interface Notice {
    NoticeId: number;
    ClientId: number;
    NoticeType: NoticeType;
    Title: string;
    Description: string | null;
    ContentJSON: string; // JSON string that parses to NoticeContentJSON
    Status: NoticeStatus;
    CreatedBy: number;
    CreatedDate: string; // ISO date string
    ModifiedBy: number | null;
    ModifiedDate: string | null; // ISO date string
}

/**
 * Notice DP Assignment interface
 */
export interface NoticeDPAssignment {
    AssignmentId: number;
    NoticeId: number;
    DPId: number;
    IsAcknowledged: boolean;
    AcknowledgedDate: string | null; // ISO date string
    AcknowledgedBy: number | null;
    AcknowledgedLanguageCode: string | null; // Language code used when DP acknowledged
    EffectiveDate: string; // ISO date string
    EmailSent: boolean;
    SmsSent: boolean;
    EmailSentDate: string | null; // ISO date string
    SmsSentDate: string | null; // ISO date string
    Status: NoticeStatus;
    AssignedBy: number;
    AssignedDate: string; // ISO date string
    ModifiedBy: number | null;
    ModifiedDate: string | null; // ISO date string
    ContentJSONSnapshot: string; // Complete snapshot of notice content (all languages) at assignment time
    SnapshotDate: string; // ISO date string - when the snapshot was created
    /** Consent notice SMS: 1 = OTP (CONSENT_NOTICE), 0 = link-only (CONSENT_NOTICE_LINK). */
    IncludeOtp?: boolean | number;
}

/**
 * Notice for creating a new notice template
 */
export interface NewNotice {
    NoticeType: NoticeType;
    Title: string;
    Description?: string | null;
    ContentJSON: NoticeContentJSON;
    Status?: NoticeStatus;
}

/**
 * Notice for updating an existing notice template
 */
export interface UpdateNotice {
    Title?: string;
    Description?: string | null;
    ContentJSON?: NoticeContentJSON;
    Status?: NoticeStatus;
}

/**
 * Notice list item (for list views with assignment info)
 * Supports both Other Notices and Consent Notices
 */
export interface NoticeListItem {
    // Source type discriminator
    sourceType?: 'NOTICE' | 'CONSENT_NOTICE';

    // Other Notice fields (when sourceType='NOTICE' or undefined for backward compatibility)
    NoticeId?: number;
    NoticeType?: NoticeType;
    Title?: string;
    Description?: string | null;
    Status?: NoticeStatus;

    // Consent Notice fields (when sourceType='CONSENT_NOTICE')
    CollectionPointId?: number;
    Name?: string; // Collection point name (instead of Title)
    CollectionPointType?: string; // Should be 'NOTICE' for consent notices
    IsActive?: boolean; // For Consent Notices
    IsPublished?: boolean; // For Consent Notices

    // Common fields
    ClientId: number;
    CreatedDate: string; // ISO date string
    CreatedBy: number;
    CreatedByName?: string;
    
    // Assignment info (from NoticeDPAssignments)
    AssignmentId?: number;
    DPId?: number;
    DPIdentifier?: string;
    IsAcknowledged?: boolean;
    AcknowledgedDate?: string | null; // ISO date string
    AcknowledgedLanguageCode?: string | null; // Language code used when DP acknowledged
    EffectiveDate?: string; // ISO date string
    EmailSent?: boolean;
    SmsSent?: boolean;
    AssignmentStatus?: NoticeStatus;
    AssignedDate?: string; // ISO date string
    AssignedBy?: number;
    AssignedByName?: string;
    ContentJSONSnapshot?: string | null; // Snapshot of notice content (for DP viewing, null for Consent Notices)
    SnapshotDate?: string | null; // ISO date string - when snapshot was created (null for Consent Notices)
    
    // Aggregated counts (when showing all assignments for a notice)
    TotalAssignments?: number;
    AcknowledgedCount?: number;
    UnacknowledgedCount?: number;
}

/**
 * Filters for listing notices
 */
export interface NoticeFilters {
    dpId?: number;
    noticeType?: NoticeType;
    status?: NoticeStatus; // Notice template status
    assignmentStatus?: NoticeStatus; // Assignment status
    isAcknowledged?: boolean;
    emailSent?: boolean;
    smsSent?: boolean;
    startDate?: string; // ISO date string
    endDate?: string; // ISO date string
    dateField?: 'EffectiveDate' | 'CreatedDate' | 'AcknowledgedDate' | 'AssignedDate';
    search?: string;
    createdBy?: number;
    assignedBy?: number;
    includeConsentNotices?: boolean; // Include Consent Notices (Collection Points with type='NOTICE')
}

/**
 * Pagination parameters
 */
export interface NoticePagination {
    page: number;
    pageSize: number;
}

/**
 * Sort options for notices
 */
export type NoticeSortBy = 'newest' | 'oldest' | 'effectiveDate' | 'title' | 'type' | 'assignedDate';

/**
 * Response for notices list query
 */
export interface NoticesListData {
    notices: NoticeListItem[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface NoticesListResponse {
    success: boolean;
    data: NoticesListData;
    error?: string;
    errorType?: string;
}

/**
 * Purpose consent status for a specific purpose
 */
export interface PurposeConsentStatus {
    purposeKey: string;
    purposeName: string;
    isRequired: boolean;
    consented: boolean;
    consentValue: boolean | null; // null if no consent record exists
    retentionType: string; // 'TIME_BASED', 'PURPOSE_BASED', 'LEGAL_REQUIREMENT', 'INDEFINITE'
    retentionExpirationDate: string | null; // ISO date string
    isExpired: boolean; // true if TIME_BASED and expired
    consentHeaderId: number | null;
    consentTimestamp: string | null; // ISO date string
}

/**
 * Assignment completion status response
 */
export interface AssignmentCompletionStatus {
    assignmentId: number;
    collectionPointId: number;
    dpId: number;
    assignedDate: string; // ISO date string
    status: 'COMPLETED' | 'PENDING';
    completedPurposes: number; // Count of required purposes that are consented
    totalRequiredPurposes: number; // Total count of required purposes
    consentHeaderId: number | null; // Most recent consent header ID if exists
    consentTimestamp: string | null; // ISO date string
    purposeStatuses: PurposeConsentStatus[]; // Status for each required purpose
}

/**
 * Bulk assignment request
 */
export interface BulkAssignNoticesRequest {
    noticeIds: number[];
    dpIds: number[];
    effectiveDate?: string; // ISO date string
    deliveryMethod?: 'SMS' | 'WHATSAPP' | 'BOTH';
}

export interface BulkAssignPreviewData {
    totalSelected: number;
    emailCount: number;
    phoneCount: number;
    blockedCount: number;
    emailDpIds: number[];
    phoneDpIds: number[];
    blocked: { dpId: number; reason: string }[];
}

export interface BulkAssignPreviewResponse {
    success: boolean;
    data: BulkAssignPreviewData;
    error?: string;
}

/**
 * Assignment result
 */
export interface NoticeAssignmentResult {
    noticeId: number;
    dpId: number;
    assignmentId?: number; // New assignment ID if successful
    success: boolean;
    error?: string;
    channel?: 'EMAIL' | 'PHONE' | 'BLOCKED';
    warnings?: string[];
}

/**
 * Bulk assignment response
 */
export interface BulkAssignNoticesData {
    totalRequested: number;
    successful: number;
    failed: number;
    results: NoticeAssignmentResult[];
}

export interface BulkAssignNoticesResponse {
    success: boolean;
    data: BulkAssignNoticesData;
    error?: string;
    errorType?: string;
}

/**
 * Assign notice request (legacy - for Other Notices only)
 */
export interface AssignNoticeRequest {
    dpId: number;
    effectiveDate?: string; // ISO date string
}

/**
 * Unified assign notice request (supports both Other Notices and Consent Notices)
 */
export interface UnifiedAssignNoticeRequest {
    sourceType: 'NOTICE' | 'CONSENT_NOTICE';
    noticeId?: number; // Required if sourceType='NOTICE'
    collectionPointId?: number; // Required if sourceType='CONSENT_NOTICE'
    dpId: number;
    effectiveDate?: string; // ISO date string (only when custom effective date is set)
    deliveryMethod?: 'SMS' | 'WHATSAPP' | 'BOTH' | 'EMAIL'; // Phone delivery when recipient is phone-based (NOTICE or CONSENT_NOTICE)
    /** Consent + SMS path: include OTP in SMS when effective date is same day as assign. */
    includeOtp?: boolean;
}

/**
 * Acknowledge notice request
 */
export interface AcknowledgeNoticeRequest {
    dpId: number;
    languageCode: string; // Language code (e.g., 'en', 'hi') used by DP when acknowledging
}

/**
 * Archive notice request
 */
export interface ArchiveNoticeRequest {
    reason?: string;
}

// Response wrappers
export interface NoticeResponse {
    success: boolean;
    data: Notice;
    error?: string;
    errorType?: string;
}

export interface NoticeAssignmentResponse {
    success: boolean;
    data: NoticeDPAssignment;
    error?: string;
    errorType?: string;
}

export interface NoticeUnifiedAssignApiResponse {
    success: boolean;
    data: NoticeDPAssignment;
    warnings?: string[];
    error?: string;
    errorType?: string;
    message?: string;
}

export interface NoticesResponse {
    success: boolean;
    data: NoticeListItem[];
    error?: string;
    errorType?: string;
}

// ============================================================================
// SERVICE METHODS
// ============================================================================

export const noticesService = {
    /**
     * Create a new notice template (no DP assignment)
     */
    createNotice: async (notice: NewNotice): Promise<Notice> => {
        try {
            const response = await apiClient.post<NoticeResponse>(
                API_PATHS.BASE,
                notice
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to create notice');
            }

            return response.data;
        } catch (error) {
            console.error('Error creating notice:', error);
            throw error;
        }
    },

    /**
     * Get notice template by ID
     */
    getNoticeById: async (noticeId: number): Promise<Notice> => {
        try {
            const response = await apiClient.get<NoticeResponse>(
                API_PATHS.BY_ID(noticeId)
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch notice');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting notice:', error);
            throw error;
        }
    },

    /**
     * Get notices list with filters, pagination, and sorting
     */
    getNoticesList: async (
        filters: NoticeFilters,
        pagination: NoticePagination,
        sortBy: NoticeSortBy = 'newest'
    ): Promise<NoticesListData> => {
        try {
            const params = new URLSearchParams();

            // Add pagination
            params.append('page', pagination.page.toString());
            params.append('pageSize', pagination.pageSize.toString());

            // Add sorting
            params.append('sortBy', sortBy);

            // Add filters
            if (filters.dpId !== undefined) {
                params.append('dpId', filters.dpId.toString());
            }
            if (filters.noticeType) {
                params.append('noticeType', filters.noticeType);
            }
            if (filters.status) {
                params.append('status', filters.status);
            }
            if (filters.assignmentStatus) {
                params.append('assignmentStatus', filters.assignmentStatus);
            }
            if (filters.isAcknowledged !== undefined) {
                params.append('isAcknowledged', filters.isAcknowledged.toString());
            }
            if (filters.emailSent !== undefined) {
                params.append('emailSent', filters.emailSent.toString());
            }
            if (filters.smsSent !== undefined) {
                params.append('smsSent', filters.smsSent.toString());
            }
            if (filters.startDate) {
                params.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                params.append('endDate', filters.endDate);
            }
            if (filters.dateField) {
                params.append('dateField', filters.dateField);
            }
            if (filters.search) {
                params.append('search', filters.search);
            }
            if (filters.createdBy !== undefined) {
                params.append('createdBy', filters.createdBy.toString());
            }
            if (filters.assignedBy !== undefined) {
                params.append('assignedBy', filters.assignedBy.toString());
            }
            if (filters.includeConsentNotices !== undefined) {
                params.append('includeConsentNotices', filters.includeConsentNotices.toString());
            }

            const response = await apiClient.get<NoticesListResponse>(
                `${API_PATHS.BASE}?${params.toString()}`
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch notices');
            }

            return response.data;
        } catch (error) {
            console.error('Error listing notices:', error);
            throw error;
        }
    },

    /**
     * Update an existing notice template
     */
    updateNotice: async (noticeId: number, update: UpdateNotice): Promise<Notice> => {
        try {
            const response = await apiClient.put<NoticeResponse>(
                API_PATHS.BY_ID(noticeId),
                update
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to update notice');
            }

            return response.data;
        } catch (error) {
            console.error('Error updating notice:', error);
            throw error;
        }
    },

    /**
     * Archive a notice template (soft delete)
     */
    archiveNotice: async (noticeId: number, reason?: string): Promise<Notice> => {
        try {
            const request: ArchiveNoticeRequest = {};
            if (reason) {
                request.reason = reason;
            }

            const response = await apiClient.delete<NoticeResponse>(
                API_PATHS.BY_ID(noticeId),
                { data: request }
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to archive notice');
            }

            return response.data;
        } catch (error) {
            console.error('Error archiving notice:', error);
            throw error;
        }
    },

    /**
     * Assign a notice template to a Data Principal (creates assignment record)
     */
    assignNoticeToDP: async (
        noticeId: number,
        dpId: number,
        effectiveDate?: string
    ): Promise<NoticeDPAssignment> => {
        try {
            const request: AssignNoticeRequest = {
                dpId,
                effectiveDate
            };

            const response = await apiClient.post<NoticeAssignmentResponse>(
                API_PATHS.ASSIGN(noticeId),
                request
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to assign notice');
            }

            return response.data;
        } catch (error) {
            console.error('Error assigning notice to DP:', error);
            throw error;
        }
    },

    /**
     * Preview bulk assign recipient breakup (email / phone / blocked)
     */
    bulkAssignPreview: async (dpIds: number[]): Promise<BulkAssignPreviewData> => {
        try {
            const response = await apiClient.post<BulkAssignPreviewResponse>(
                API_PATHS.BULK_ASSIGN_PREVIEW,
                { dpIds }
            );
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to load bulk assign preview');
            }
            return response.data;
        } catch (error) {
            console.error('Error in bulk assign preview:', error);
            throw error;
        }
    },

    /**
     * Bulk assign notices to multiple Data Principals
     */
    bulkAssignNotices: async (
        request: BulkAssignNoticesRequest
    ): Promise<BulkAssignNoticesData> => {
        try {
            const response = await apiClient.post<BulkAssignNoticesResponse>(
                API_PATHS.BULK_ASSIGN,
                request
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to bulk assign notices');
            }

            return response.data;
        } catch (error: unknown) {
            console.error('Error in bulk assign notices:', error);
            throw new Error(getApiErrorMessage(error, 'Failed to bulk assign notices'));
        }
    },

    /**
     * Acknowledge a notice assignment (called by DP)
     * Note: Now uses assignmentId instead of noticeId
     * @param languageCode - Language code (e.g., 'en', 'hi') used by DP when acknowledging
     */
    acknowledgeNotice: async (assignmentId: number, dpId: number, languageCode: string): Promise<NoticeDPAssignment> => {
        try {
            if (!languageCode || languageCode.trim().length === 0) {
                throw new Error('languageCode is required for acknowledgment');
            }

            const request: AcknowledgeNoticeRequest = { 
                dpId,
                languageCode: languageCode.trim()
            };

            const response = await apiClient.post<NoticeAssignmentResponse>(
                API_PATHS.ACKNOWLEDGE(assignmentId),
                request
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to acknowledge notice');
            }

            return response.data;
        } catch (error) {
            console.error('Error acknowledging notice:', error);
            throw error;
        }
    },

    /**
     * Get all notices for a specific Data Principal (via assignments)
     */
    getDPNotices: async (dpId: number, includeArchived = false): Promise<NoticeListItem[]> => {
        try {
            const params = new URLSearchParams();
            if (includeArchived) {
                params.append('includeArchived', 'true');
            }

            const response = await apiClient.get<NoticesResponse>(
                `${API_PATHS.DP_NOTICES(dpId)}?${params.toString()}`
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch DP notices');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting DP notices:', error);
            throw error;
        }
    },

    /**
     * Get assignment by ID
     */
    getAssignmentById: async (assignmentId: number): Promise<NoticeDPAssignment> => {
        try {
            const response = await apiClient.get<NoticeAssignmentResponse>(
                API_PATHS.ASSIGNMENT_BY_ID(assignmentId)
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch assignment');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting assignment:', error);
            throw error;
        }
    },

    /**
     * Assign a notice (Other Notice or Consent Notice) to a Data Principal (unified endpoint)
     */
    assignNoticeToDPUnified: async (
        request: UnifiedAssignNoticeRequest
    ): Promise<{ assignment: NoticeDPAssignment; warnings: string[] }> => {
        try {
            const response = await apiClient.post<NoticeUnifiedAssignApiResponse>(
                API_PATHS.ASSIGN_UNIFIED,
                request
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to assign notice');
            }

            return {
                assignment: response.data,
                warnings: response.warnings ?? []
            };
        } catch (error: unknown) {
            console.error('Error assigning notice to DP (unified):', error);
            throw new Error(getApiErrorMessage(error, 'Failed to assign notice'));
        }
    },

    /**
     * Resend notice assignment notification
     */
    resendNoticeAssignment: async (
        assignmentId: number,
        options?: { deliveryMethod?: 'SMS' | 'WHATSAPP' | 'BOTH' }
    ): Promise<{
        success: boolean;
        method: 'EMAIL' | 'PHONE' | 'NONE';
        message?: string;
        warnings?: string[];
    }> => {
        try {
            const response = await apiClient.post<{
                success: boolean;
                method: 'EMAIL' | 'PHONE' | 'NONE';
                message?: string;
                warnings?: string[];
            }>(
                API_PATHS.RESEND_ASSIGNMENT(assignmentId),
                options?.deliveryMethod ? { deliveryMethod: options.deliveryMethod } : {}
            );

            return response;
        } catch (error: any) {
            console.error('Error resending notice assignment:', error);
            if (error?.response?.status === 429) {
                const retryAfterSeconds = parseNoticeResendRetryAfterSeconds(error);
                const serverText =
                    (typeof error?.response?.data?.error === 'string' && error.response.data.error.trim()) ||
                    '';
                const waitText = formatNoticeResendWait(retryAfterSeconds);
                const message = serverText
                    ? `${serverText} Try again in about ${waitText}.`
                    : `Too soon to resend this notification. Try again in about ${waitText}.`;
                const err = new Error(message) as Error & {
                    isResendThrottle?: boolean;
                    retryAfterSeconds?: number;
                };
                err.isResendThrottle = true;
                err.retryAfterSeconds = retryAfterSeconds;
                throw err;
            }
            throw new Error(getApiErrorMessage(error, 'Failed to resend notice'));
        }
    },

    /**
     * Check assignment completion status (Option B logic)
     */
    checkAssignmentStatus: async (assignmentId: number): Promise<AssignmentCompletionStatus> => {
        try {
            const response = await apiClient.get<{ success: boolean; data: AssignmentCompletionStatus }>(
                API_PATHS.ASSIGNMENT_STATUS(assignmentId)
            );

            if (!response.success || !response.data) {
                throw new Error('Failed to check assignment status');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error checking assignment status:', error);
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.message || 
                                'Failed to check assignment status';
            throw new Error(errorMessage);
        }
    },

    /**
     * Get purpose-by-purpose consent statuses for a DP and CollectionPoint
     */
    getPurposeStatuses: async (
        collectionPointId: number,
        dpId: number
    ): Promise<PurposeConsentStatus[]> => {
        try {
            const params = new URLSearchParams({
                collectionPointId: collectionPointId.toString(),
                dpId: dpId.toString()
            });

            const response = await apiClient.get<{ success: boolean; data: PurposeConsentStatus[] }>(
                `${API_PATHS.PURPOSE_STATUSES}?${params.toString()}`
            );

            if (!response.success || !response.data) {
                throw new Error('Failed to fetch purpose statuses');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error getting purpose statuses:', error);
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.message || 
                                'Failed to fetch purpose statuses';
            throw new Error(errorMessage);
        }
    },

    /**
     * Verify OTP and create consent for a consent notice assignment
     * @param assignmentId - The assignment ID
     * @param otp - The 6-digit OTP
     * @returns Promise with consent details
     */
    verifyOtp: async (
        assignmentId: number,
        otp: string
    ): Promise<{
        consentHeaderId: number;
        dataPrincipalId: number;
        assignmentId: number;
    }> => {
        try {
            const response = await apiClient.post<{
                success: boolean;
                message?: string;
                data?: {
                    consentHeaderId: number;
                    dataPrincipalId: number;
                    assignmentId: number;
                };
                error?: string;
            }>(API_PATHS.VERIFY_OTP(assignmentId), {
                otp
            });

            if (!response.success) {
                throw new Error(response.error || 'Failed to verify OTP');
            }

            if (!response.data) {
                throw new Error('No data returned from OTP verification');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error verifying OTP:', error);
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.message || 
                                'Failed to verify OTP';
            throw new Error(errorMessage);
        }
    },

    /**
     * Resend OTP for consent notice assignment
     * @param assignmentId - The assignment ID
     */
    resendOtp: async (assignmentId: number): Promise<void> => {
        try {
            const response = await apiClient.post<{
                success: boolean;
                message?: string;
                error?: string;
            }>(API_PATHS.RESEND_OTP(assignmentId));

            if (!response.success) {
                throw new Error(response.error || 'Failed to resend OTP');
            }
        } catch (error: any) {
            console.error('Error resending OTP:', error);
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.message || 
                                'Failed to resend OTP';
            throw new Error(errorMessage);
        }
    }
};
